import { InformationCircleIcon } from '@heroicons/react/24/solid'
import cn from 'classnames'
import {
  forwardRef,
  useState,
  useEffect,
  type ReactNode,
  type FocusEvent,
  type ForwardedRef,
  type InputHTMLAttributes,
} from 'react'
import { twMerge } from 'tailwind-merge'
import PhoneInput from 'react-phone-number-input/input'
import { type DefaultInputComponentProps } from 'react-phone-number-input'
import { type DefaultProps } from '../common'

export interface InputPhoneProps
  extends DefaultInputComponentProps,
    DefaultProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string
  error?: string
  icon?: ReactNode
  isPII?: boolean
}

const InputPhone = forwardRef<typeof PhoneInput, InputPhoneProps>(
  (
    { label, error, className, disabled, icon, isPII, ...props }: InputPhoneProps,
    ref: ForwardedRef<typeof PhoneInput>,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const [hasValue, setHasValue] = useState(Boolean(props.value))

    useEffect(() => {
      setHasValue(Boolean(props.value))
    }, [props.value])

    const inputClasses = twMerge(
      cn('border leading-6 px-4 rounded w-full disabled:text-gray-400 disabled:bg-gray-50', 'text-lg text-body', {
        'pt-[28px] pb-[12px]': Boolean(label),
        'pt-[20px] pb-[20px]': !label,
        'border-gray-200 outline-none focus:border-gray-800 hover:border-gray-400 disabled:border-gray-300': !error,
        'border-red-700 pr-9 outline-none focus:ring-red-500 bg-red-50 disabled:border-gray-300': Boolean(error),
        'cursor-not-allowed touch-none': Boolean(disabled),
        'fs-exclude': isPII,
      }),

      className,
    )

    const labelClasses = cn(
      'pointer-events-none absolute ml-4 text-base transition-all duration-200 ease-in-out origin-top-left',
      {
        'translate-y-[22px]': !isFocused && !hasValue,
        'font-semibold scale-75 translate-y-[10px]': isFocused || hasValue,
        'text-gray-500': !isFocused,
        'text-gray-900': isFocused,
        'opacity-70': disabled,
      },
    )

    return (
      <div>
        <div className="relative">
          {label ? <span className={labelClasses}>{label}</span> : null}
          <PhoneInput
            {...props}
            ref={ref}
            disabled={disabled}
            className={inputClasses}
            defaultCountry="US"
            value={props.value as string}
            onFocus={(event: FocusEvent<HTMLInputElement>) => {
              setIsFocused(true)
              props.onFocus?.(event)
            }}
            onBlur={(event: FocusEvent<HTMLInputElement>) => {
              setIsFocused(false)
              props.onBlur?.(event)
            }}
            onChange={(value) => {
              setHasValue(Boolean(value))
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              props.onChange?.(value as string)
            }}
            maxLength={14}
          />
          {error ? (
            <span
              className={cn('absolute right-3 top-1/2 -translate-y-1/2 transform pointer-events-none', {
                'right-3': !icon,
                'right-9': Boolean(icon),
              })}
            >
              <InformationCircleIcon className="w-5 h-5 fill-red-400" />
            </span>
          ) : null}
          {icon ? (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 transform pointer-events-none">{icon}</span>
          ) : null}
        </div>
        {Boolean(error) && <p className="text-text-error typo-primary-sm-semibold mt-3xs">{error}</p>}
      </div>
    )
  },
)

InputPhone.displayName = 'InputPhone'

export { InputPhone }
